
import { useState } from 'react';

const BENEFITS = [
  'Free needs assessment with no obligation',
  'Custom training solution proposal within 48 hrs',
  'SETA grant guidance included',
  'Flexible scheduling and delivery options',
];

const CONSULTATION_EMAIL = 'info@sylman.co.za';

const inputCls = (err: boolean) =>
  `w-full px-4 py-3 rounded-xl border text-sm outline-none transition-all duration-150 ${
    err
      ? 'border-red-400 bg-red-50 focus:ring-2 focus:ring-red-300'
      : 'border-[#e8eaea] hover:border-[#c8e6cf] focus:ring-2 focus:ring-[#2D7F3E]/30 focus:border-[#2D7F3E]'
  }`;

export default function FinalCTASection() {
  const [form,   setForm]   = useState({ name: '', email: '', company: '', phone: '', message: '' });
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [sending, setSending] = useState(false);
  const [status,  setStatus]  = useState('');

  const errors: Record<string, string> = {};
  if (!form.name.trim())  errors.name  = 'Name is required';
  if (!form.email.trim()) errors.email = 'Email is required';
  else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errors.email = 'Enter a valid email';

  const change = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm(p => ({ ...p, [e.target.name]: e.target.value }));
  const blur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setTouched(p => ({ ...p, [e.target.name]: true }));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setTouched({ name: true, email: true });
    if (Object.keys(errors).length) return;
    setSending(true);
    try {
      const body = new URLSearchParams();
      Object.entries(form).forEach(([k, v]) => body.append(k, v));
      body.append('formType', 'Free Consultation Request');
      const res = await fetch('/api/contact.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      setStatus(res.ok ? 'success' : 'error');
      if (res.ok) { setForm({ name: '', email: '', company: '', phone: '', message: '' }); setTouched({}); }
    } catch { setStatus('error'); }
    finally { setSending(false); }
  };

  return (
    <section className="section-wrap bg-[#1A3A22] relative overflow-hidden">
      <div className="absolute inset-0 opacity-[0.03]"
        style={{ backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)', backgroundSize: '28px 28px' }} />

      <div className="section-inner relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">

          {/* Left */}
          <div className="text-white reveal-left">
            <p className="section-label" style={{ color: '#5EC97A' }}>Free Consultation</p>
            <h2 className="font-serif font-normal text-3xl md:text-4xl leading-tight mb-6" style={{ color: 'white' }}>
              Ready to Develop Your Workforce?
            </h2>
            <p className="text-white/60 text-base leading-relaxed mb-10">
              Take the first step with a free, no-obligation consultation. We'll assess your needs and propose a tailored solution within 48 hours.
            </p>

            <ul className="space-y-4 mb-12">
              {BENEFITS.map(b => (
                <li key={b} className="flex items-start gap-3">
                  <div className="w-5 h-5 rounded-full bg-[#2D7F3E] flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-check-line text-white text-[11px]" />
                  </div>
                  <span className="text-white/75 text-sm">{b}</span>
                </li>
              ))}
            </ul>

            <div className="flex items-center gap-4 pt-8 border-t border-white/10">
              <div className="w-11 h-11 rounded-xl bg-white/10 flex items-center justify-center flex-shrink-0">
                <i className="ri-phone-line text-[#5EC97A] text-lg" />
              </div>
              <div>
                <div className="text-xs text-white/40 uppercase tracking-wider mb-0.5">Prefer to call?</div>
                <a href="tel:+27102240406" className="text-white font-semibold hover:text-[#5EC97A] transition-colors">
                  +27 10 224 0406
                </a>
              </div>
            </div>
          </div>

          {/* Right — form */}
          <div className="reveal-right">
            {status === 'success' ? (
              <div className="bg-white rounded-2xl p-10 text-center">
                <div className="w-16 h-16 bg-[#f0f9f2] rounded-full flex items-center justify-center mx-auto mb-5">
                  <i className="ri-check-line text-[#2D7F3E] text-3xl" />
                </div>
                <h3 className="font-serif text-xl font-bold text-[#111111] mb-2">Request Received!</h3>
                <p className="text-[#6b7280] text-sm mb-6">
                  Thank you! We'll be in touch within 24 business hours.
                </p>
                <button onClick={() => setStatus('')} className="text-sm text-[#2D7F3E] font-semibold hover:underline cursor-pointer">
                  Submit another request
                </button>
              </div>
            ) : (
              <div className="bg-white rounded-2xl p-8 shadow-2xl">
                <h3 className="font-serif text-xl font-bold text-[#111111] mb-1">Request Free Consultation</h3>
                <p className="text-[#9ca3af] text-xs mb-7">We respond within 24 hours on business days.</p>

                <form onSubmit={submit} noValidate>
                  <input type="text" name="website" tabIndex={-1} autoComplete="off" aria-hidden="true" className="absolute -left-[9999px]" />
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-xs font-bold text-[#3d3f42] mb-1.5 uppercase tracking-wide">
                        Full Name <span className="text-red-500">*</span>
                      </label>
                      <input type="text" name="name" value={form.name} onChange={change} onBlur={blur}
                        placeholder="Your name"
                        className={inputCls(!!(touched.name && errors.name))} />
                      {touched.name && errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-[#3d3f42] mb-1.5 uppercase tracking-wide">
                        Email <span className="text-red-500">*</span>
                      </label>
                      <input type="email" name="email" value={form.email} onChange={change} onBlur={blur}
                        placeholder="your@email.com"
                        className={inputCls(!!(touched.email && errors.email))} />
                      {touched.email && errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-xs font-bold text-[#3d3f42] mb-1.5 uppercase tracking-wide">Company</label>
                      <input type="text" name="company" value={form.company} onChange={change}
                        placeholder="Company name" className={inputCls(false)} />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-[#3d3f42] mb-1.5 uppercase tracking-wide">Phone</label>
                      <input type="tel" name="phone" value={form.phone} onChange={change}
                        placeholder="+27 XX XXX XXXX" className={inputCls(false)} />
                    </div>
                  </div>

                  <div className="mb-6">
                    <label className="block text-xs font-bold text-[#3d3f42] mb-1.5 uppercase tracking-wide">
                      Training Requirements
                    </label>
                    <textarea name="message" value={form.message} onChange={change} rows={3} maxLength={500}
                      placeholder="Tell us about your training needs…"
                      className={`${inputCls(false)} resize-none`} />
                  </div>

                  <button type="submit" className="btn btn-primary w-full btn-lg" disabled={sending}>
                    {sending
                      ? <><i className="ri-loader-4-line animate-spin mr-2" />Sending…</>
                      : <><i className="ri-calendar-check-line mr-2" />Request Free Consultation</>}
                  </button>

                  {status === 'error' && (
                    <p className="mt-4 text-sm text-red-600 text-center">
                      Something went wrong. Please try again or email {CONSULTATION_EMAIL}.
                    </p>
                  )}

                  <p className="text-center text-xs text-[#9ca3af] mt-4 flex items-center justify-center gap-1">
                    <i className="ri-lock-line" />Your information is secure and will never be shared.
                  </p>
                </form>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
